import { useState } from 'react';
import { breweries } from '../../../mocks/breweries';

const BeerMap = () => {
  const [activeBrewery, setActiveBrewery] = useState(breweries[0]);

  return (
    <section id="beer-map" className="w-full bg-[#1a1210] py-24">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="flex items-center gap-4 mb-12">
          <div className="w-1 h-8 bg-[#f5a623] rounded-full" />
          <div>
            <p className="text-[#f5a623] text-xs font-['Roboto'] font-bold tracking-[4px] uppercase mb-1">
              Curated Finds
            </p>
            <h2 className="font-['Bebas_Neue'] text-5xl text-white tracking-wider">
              BEER MAP
            </h2>
          </div>
        </div>

        <p className="text-[#9a9a9a] text-sm font-['Roboto'] italic mb-10 ml-5">
          Every brewery that made it onto the channel. All worth the drive.
        </p>

        {/* Main Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Brewery List (Left) */}
          <div className="lg:col-span-2 flex flex-col gap-3">
            {breweries.map((b) => (
              <button
                key={b.id}
                onClick={() => setActiveBrewery(b)}
                className={`text-left p-5 rounded-xl border transition-all duration-200 cursor-pointer w-full group ${
                  activeBrewery.id === b.id
                    ? 'bg-[#f5a623]/10 border-[#f5a623]/60'
                    : 'bg-[#1f1c1a] border-[#2a2a2a] hover:border-[#f5a623]/30'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3
                      className={`font-['Roboto'] font-bold text-sm leading-snug mb-1 ${
                        activeBrewery.id === b.id ? 'text-[#f5a623]' : 'text-white group-hover:text-[#f5a623]'
                      } transition-colors`}
                    >
                      {b.name}
                    </h3>
                    <p className="text-[#6a6a6a] text-xs font-['Roboto'] flex items-center gap-1">
                      <i className="ri-map-pin-line text-xs" />
                      {b.location}
                    </p>
                    <p className="text-[#9a9a9a] text-xs font-['Roboto_Mono'] mt-1 truncate">
                      🍺 {b.featuredBeer}
                    </p>
                  </div>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <div key={s} className="w-3 h-3 flex items-center justify-center">
                        <i className={`ri-star-fill text-[10px] ${s <= b.rating ? 'text-[#f5a623]' : 'text-[#2a2a2a]'}`} />
                      </div>
                    ))}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Map + Detail (Right) */}
          <div className="lg:col-span-3 flex flex-col gap-5">
            {/* Google Map Embed */}
            <div className="w-full h-64 rounded-xl overflow-hidden border border-[#2a2a2a]">
              <iframe
                title={`Map – ${activeBrewery.name}`}
                width="100%"
                height="100%"
                frameBorder="0"
                src={`https://www.google.com/maps/embed/v1/search?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU3bR4&q=${activeBrewery.mapEmbedQuery}&zoom=13`}
                allowFullScreen
              />
            </div>

            {/* Brewery Detail Card */}
            <div className="bg-[#1f1c1a] rounded-xl border border-[#2a2a2a] p-6 flex flex-col gap-5">
              {/* Brewery Header */}
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-white font-['Roboto'] font-bold text-xl">{activeBrewery.name}</h3>
                  <p className="text-[#9a9a9a] text-sm font-['Roboto'] flex items-center gap-1 mt-1">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-map-pin-2-fill text-[#f5a623] text-xs" />
                    </div>
                    {activeBrewery.location}
                  </p>
                </div>
                <div className="bg-[#f5a623]/10 border border-[#f5a623]/30 rounded-lg px-3 py-1.5 text-center">
                  <div className="flex items-center gap-0.5 justify-center">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <div key={s} className="w-3 h-3 flex items-center justify-center">
                        <i className={`ri-star-fill text-xs ${s <= activeBrewery.rating ? 'text-[#f5a623]' : 'text-[#2a2a2a]'}`} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Featured Beer */}
              <div className="bg-[#111111] rounded-lg p-4 flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-[#f5a623]/10 rounded-lg">
                  <i className="ri-goblet-fill text-[#f5a623]" />
                </div>
                <div>
                  <p className="text-[#6a6a6a] text-xs font-['Roboto'] tracking-[2px] uppercase">Featured Beer</p>
                  <p className="text-[#f5a623] font-['Roboto'] font-bold text-sm">{activeBrewery.featuredBeer}</p>
                </div>
              </div>

              {/* Notes */}
              <p className="text-[#c4c4c4] text-sm font-['Merriweather'] italic leading-relaxed border-l-2 border-[#f5a623]/40 pl-4">
                "{activeBrewery.notes}"
              </p>

              {/* Episode Link */}
              <div className="flex items-center justify-between pt-2 border-t border-[#2a2a2a]">
                <span className="text-[#6a6a6a] text-xs font-['Roboto_Mono']">
                  {activeBrewery.episode}
                </span>
                <a
                  href={activeBrewery.episodeUrl}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="flex items-center gap-1.5 text-[#f5a623] hover:text-white text-xs font-['Roboto'] font-bold transition-colors cursor-pointer whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-youtube-fill text-sm" />
                  </div>
                  Watch Episode
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BeerMap;
